import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { orderService, Order } from "../../api/orderService";

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f8f8f8;
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #e97d7dff;
  margin-bottom: 1rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const OrderCard = styled.div`
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 24px rgba(233, 125, 125, 0.15);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: #f9f9f9;
  border-bottom: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
    align-items: flex-start;
    padding: 1rem;
  }
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const OrderId = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin: 0;
  font-weight: 600;
`;

const OrderDate = styled.p`
  color: #666;
  font-size: 0.95rem;
  margin: 0;
`;

const OrderTotal = styled.p`
  color: #e97d7dff;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  background: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#d4edda";
      case "shipped":
        return "#cce5ff";
      case "processing":
        return "#fff3cd";
      case "cancelled":
        return "#f8d7da";
      default:
        return "#e9ecef";
    }
  }};

  color: ${(props) => {
    switch (props.status) {
      case "delivered":
        return "#155724";
      case "shipped":
        return "#004085";
      case "processing":
        return "#856404";
      case "cancelled":
        return "#721c24";
      default:
        return "#495057";
    }
  }};
`;

const OrderItems = styled.div`
  padding: 1rem 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ItemsTitle = styled.h4`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0.8rem;
  background: #f8f9fa;
  border-radius: 0.6rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.6rem;
  }
`;

const ItemImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 0.4rem;
  background: #f7f7f7;

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const ItemDetails = styled.div`
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ItemName = styled.h5`
  font-size: 1rem;
  color: #333;
  margin: 0 0 0.3rem 0;
  font-weight: 600;
`;

const ItemQuantity = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin: 0;
`;

const ItemPrice = styled.div`
  text-align: right;

  @media (max-width: 768px) {
    text-align: left;
    width: 100%;
  }
`;

const ItemTotalPrice = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: #e97d7dff;
  margin: 0;
`;

const OrderFooter = styled.div`
  padding: 1rem 1.5rem;
  background: #f9f9f9;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.8rem;
    align-items: stretch;
    padding: 1rem;
  }
`;

const ShippingInfo = styled.div`
  color: #666;
  font-size: 0.95rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  background: ${(props) =>
    props.variant === "primary" ? "#e97d7dff" : "#f8f9fa"};
  color: ${(props) => (props.variant === "primary" ? "#fff" : "#333")};
  border: ${(props) =>
    props.variant === "secondary" ? "1px solid #e0e0e0" : "none"};
  border-radius: 0.5rem;
  padding: 0.8rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.variant === "primary" ? "#d66a6a" : "#e9ecef"};
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 0.9rem 1.2rem;
    font-size: 1rem;
  }
`;

const CancelLink = styled.button<{ disabled?: boolean }>`
  background: none;
  border: none;
  color: #dc3545;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  transition: all 0.2s ease;
  padding: 0.5rem 0;

  &:hover {
    color: #c82333;
    text-decoration: underline;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const EmptyMessage = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const EmptyButton = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 0.8rem;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #d66a6a;
    transform: translateY(-1px);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e97d7dff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const Orders: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingOrder, setCancellingOrder] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState<string | null>(null);

  useEffect(() => {
    // Fetch user's orders from API
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const userOrders = await orderService.getUserOrders();
        setOrders(userOrders);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to load orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    if (user) {
      fetchOrders();
    }
  }, [user, navigate]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "delivered":
        return "Delivered";
      case "shipped":
        return "Shipped";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      case "cancelled":
        return "Cancelled";
      default:
        return status;
    }
  };

  const getOrderTotal = (order: Order) => {
    return order.totalAmount || order.total || 0;
  };

  const handleCancelOrder = (orderId: string) => {
    setOrderToCancel(orderId);
    setShowCancelDialog(true);
  };

  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      setCancellingOrder(orderToCancel);
      await orderService.cancelOrder(orderToCancel);

      // Update the order in the local state
      setOrders(
        orders.map((order) =>
          order.id === orderToCancel ? { ...order, status: "cancelled" } : order
        )
      );

      setShowCancelDialog(false);
      setOrderToCancel(null);
    } catch (err) {
      console.error("Error cancelling order:", err);
      setError("Failed to cancel order. Please try again.");
    } finally {
      setCancellingOrder(null);
    }
  };

  const canCancelOrder = (status: string) => {
    return ["pending", "paid", "processing"].includes(status);
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingState>
            <LoadingSpinner />
            <LoadingText>Loading your orders...</LoadingText>
          </LoadingState>
        </Container>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Container>
          <Header>
            <Title>My Orders</Title>
          </Header>
          <EmptyState>
            <EmptyIcon>⚠️</EmptyIcon>
            <EmptyTitle>Error Loading Orders</EmptyTitle>
            <EmptyMessage>{error}</EmptyMessage>
            <EmptyButton onClick={() => window.location.reload()}>
              Try Again
            </EmptyButton>
          </EmptyState>
        </Container>
      </PageWrapper>
    );
  }

  if (orders.length === 0) {
    return (
      <PageWrapper>
        <Container>
          <Header>
            <Title>My Orders</Title>
          </Header>
          <EmptyState>
            <EmptyIcon>📦</EmptyIcon>
            <EmptyTitle>No orders yet</EmptyTitle>
            <EmptyMessage>
              You haven't placed any orders yet. Start shopping to see your
              orders here!
            </EmptyMessage>
            <EmptyButton onClick={() => navigate("/shop")}>
              Start Shopping
            </EmptyButton>
          </EmptyState>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <Header>
          <Title>My Orders</Title>
        </Header>

        <OrdersList>
          {orders.map((order) => (
            <OrderCard key={order.id}>
              <OrderHeader>
                <OrderInfo>
                  <OrderId>Order #{order.id}</OrderId>
                  <OrderDate>Placed on {formatDate(order.createdAt)}</OrderDate>
                  <OrderTotal>
                    Total: ${getOrderTotal(order).toFixed(2)}
                  </OrderTotal>
                </OrderInfo>
                <StatusBadge status={order.status}>
                  {getStatusText(order.status)}
                </StatusBadge>
              </OrderHeader>

              <OrderItems>
                <ItemsTitle>Items ({order.items.length})</ItemsTitle>
                <ItemList>
                  {order.items.map((item, index: number) => (
                    <OrderItem key={index}>
                      <ItemImage src={item.image} alt={item.name} />
                      <ItemDetails>
                        <ItemName>{item.name}</ItemName>
                        <ItemQuantity>Quantity: {item.quantity}</ItemQuantity>
                      </ItemDetails>
                      <ItemPrice>
                        <ItemTotalPrice>
                          ${(item.price * item.quantity).toFixed(2)}
                        </ItemTotalPrice>
                      </ItemPrice>
                    </OrderItem>
                  ))}
                </ItemList>
              </OrderItems>

              <OrderFooter>
                <ShippingInfo>
                  <strong>Shipping to:</strong> {order.customerName},{" "}
                  {order.shippingAddress.city}, {order.shippingAddress.state},{" "}
                  {order.shippingAddress.zipCode}
                </ShippingInfo>
                <ActionButtons>
                  {canCancelOrder(order.status) && (
                    <CancelLink
                      onClick={() => handleCancelOrder(order.id)}
                      disabled={cancellingOrder === order.id}
                    >
                      {cancellingOrder === order.id
                        ? "Cancelling..."
                        : "Cancel Order"}
                    </CancelLink>
                  )}
                </ActionButtons>
              </OrderFooter>
            </OrderCard>
          ))}
        </OrdersList>
      </Container>

      {/* Cancel Order Confirmation Dialog */}
      {showCancelDialog && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "2rem",
              borderRadius: "1rem",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
            }}
          >
            <h3 style={{ margin: "0 0 1rem 0", color: "#333" }}>
              Cancel Order
            </h3>
            <p style={{ margin: "0 0 2rem 0", color: "#666" }}>
              Are you sure you want to cancel this order? This action cannot be
              undone.
            </p>
            <div
              style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
            >
              <Button
                variant="secondary"
                onClick={() => {
                  setShowCancelDialog(false);
                  setOrderToCancel(null);
                }}
                style={{ background: "#6c757d", color: "#fff" }}
              >
                Keep Order
              </Button>
              <Button
                variant="primary"
                onClick={confirmCancelOrder}
                disabled={cancellingOrder !== null}
                style={{ background: "#dc3545", color: "#fff" }}
              >
                {cancellingOrder ? "Cancelling..." : "Yes, Cancel Order"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageWrapper>
  );
};

export default Orders;
